import { Alert, Box, CircularProgress } from "@mui/material";
import useGlobalContext from "hooks/globalData";
import { useEffect } from "react";
import { useQuery } from "react-query";
import {
  ListRequestOfProcessAuditLogFilter,
  ProcessAuditLogFilter,
} from "services/KnowfullyAdminClient";
import { convertToZonedDateTime } from "utils/dateUtils";
import { FormattedMessage as T } from "react-intl";

const getStyles = () => ({
  logData: {
    m: 0,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontFamily: "monospace",
    fontSize: "0.75rem",
  },
});

const AuditLogsContent = ({ processTypeId }: { processTypeId: string }) => {
  const { loggingService } = useGlobalContext();
  const styles = getStyles();

  const { data, isLoading, isError, refetch } = useQuery(
    ["loggingService.getLatestProcessAuditLogs", processTypeId],
    () =>
      loggingService.getLatestProcessAuditLogs(
        ListRequestOfProcessAuditLogFilter.fromJS({
          parameters: ProcessAuditLogFilter.fromJS({ processTypeId }),
          sortField: "eventDate",
        })
      ),
    {
      enabled: !!processTypeId,
    }
  );

  useEffect(() => {
    if (!processTypeId) return;

    const interval = setInterval(() => {
      refetch();
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [processTypeId, refetch]);

  if (isLoading) {
    return <CircularProgress size={16} />;
  }

  if (isError) {
    return (
      <Alert severity="error">
        <T id="fetchError" />
      </Alert>
    );
  }

  const logs = data?.data ?? [];

  return (
    <Box>
      {logs.map((log) => {
        const eventDate = convertToZonedDateTime(log.eventDate);
        return (
          <Box key={log.eventDate?.toString()} m={1}>
            {eventDate} - {log.eventTypeName}
            {log.data && (
              <details>
                <summary>
                  <T id="detailsLabel" />
                </summary>
                <Box component="pre" sx={styles.logData}>
                  {log.data}
                </Box>
              </details>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default AuditLogsContent;
